import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Shield, Cookie } from "lucide-react";

const CONSENT_KEY = "ea_cookie_consent";

type ConsentChoice = "granted" | "denied";

interface ConsentState {
  analytics_storage: ConsentChoice;
  ad_storage: ConsentChoice;
  ad_user_data: ConsentChoice;
  ad_personalization: ConsentChoice;
  personalization_storage: ConsentChoice;
  timestamp: number;
}

const pushConsentUpdate = (state: Omit<ConsentState, "timestamp">) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: state.analytics_storage,
      ad_storage: state.ad_storage,
      ad_user_data: state.ad_user_data,
      ad_personalization: state.ad_personalization,
      personalization_storage: state.personalization_storage,
    });
  }
};

const getSavedConsent = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
};

const saveConsent = (state: ConsentState) => {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable
  }
};

/** Remove saved consent so the banner reappears. Call from footer link. */
export const resetCookieConsent = () => {
  try {
    localStorage.removeItem(CONSENT_KEY);
  } catch {
    // ignore
  }
  // Force page reload to re-show banner with denied defaults
  window.location.reload();
};

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  // On mount, check if consent was already given
  useEffect(() => {
    const saved = getSavedConsent();
    if (saved) {
      // Re-apply saved consent (covers page reload)
      pushConsentUpdate(saved);
      setVisible(false);
    } else {
      // Show banner after a small delay to not block LCP
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const state: ConsentState = {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      personalization_storage: "granted",
      timestamp: Date.now(),
    };
    pushConsentUpdate(state);
    saveConsent(state);
    setVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const state: ConsentState = {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      personalization_storage: "denied",
      timestamp: Date.now(),
    };
    pushConsentUpdate(state);
    saveConsent(state);
    setVisible(false);
  }, []);

  const handleAcceptEssential = useCallback(() => {
    const state: ConsentState = {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      personalization_storage: "denied",
      timestamp: Date.now(),
    };
    pushConsentUpdate(state);
    saveConsent(state);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Consentimento de cookies"
      className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-border/60 bg-background/95 backdrop-blur-lg shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
            <Cookie className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary sm:hidden" />
              <h3 className="text-sm font-semibold text-foreground">
                Privacidade e Cookies
              </h3>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Utilizamos cookies para análise de desempenho (Google Analytics) e
              publicidade (Google Ads). Pode aceitar todos, apenas os essenciais
              (analytics sem publicidade), ou recusar todos os opcionais. A sua
              escolha pode ser alterada a qualquer momento.{" "}
              <a
                href="/politica-privacidade"
                className="underline hover:text-foreground transition-colors"
              >
                Política de Privacidade
              </a>{" "}
              ·{" "}
              <a
                href="/governanca-dados"
                className="underline hover:text-foreground transition-colors"
              >
                Governança de Dados
              </a>
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                size="sm"
                onClick={handleAcceptAll}
                className="text-xs font-medium"
              >
                Aceitar Todos
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={handleAcceptEssential}
                className="text-xs font-medium"
              >
                Apenas Essenciais
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleRejectAll}
                className="text-xs font-medium"
              >
                Recusar
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
